import { useEffect, useState } from "react";
import { getDashboardKpis, formatErr } from "@/lib/staffApi";
import { Loader2, Users, ShieldCheck, TrendingUp, PieChart, Lock } from "lucide-react";

function BigNumber({ label, value, unidad, icon: Icon }) {
  return (
    <div className="bg-[#111] border border-neutral-800 rounded-xl p-5" data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-neutral-500">{label}</div>
        {Icon && <Icon size={18} className="text-orange-500" />}
      </div>
      <div className="text-3xl font-black mt-2">{value}{unidad === "%" ? "%" : ""}</div>
      {unidad && unidad !== "%" && <div className="text-xs text-neutral-500 mt-1">{unidad}</div>}
    </div>
  );
}

function Distribution({ title, data }) {
  const entries = Object.entries(data || {});
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="bg-[#111] border border-neutral-800 rounded-xl p-5" data-testid={`kpi-dist-${title}`}>
      <div className="text-sm font-semibold text-neutral-300 mb-3">{title}</div>
      {entries.length === 0 && <div className="text-sm text-neutral-500">Sin datos.</div>}
      <div className="space-y-2">
        {entries.map(([k, v]) => (
          <div key={k}>
            <div className="flex justify-between text-xs mb-1"><span className="text-neutral-400">{k}</span><span className="text-neutral-500">{v}</span></div>
            <div className="h-2 rounded bg-neutral-800 overflow-hidden">
              <div className="h-full bg-orange-500" style={{ width: `${(v / total) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StaffDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardKpis().then(setData).catch((e) => setErr(formatErr(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-neutral-400"><Loader2 className="animate-spin" /> Cargando KPIs…</div>;
  if (err) return <div className="text-red-400" data-testid="dashboard-error">{err}</div>;

  const k = data.kpis;

  return (
    <div data-testid="staff-dashboard">
      <h1 className="text-2xl font-bold mb-1">Dashboard operativo</h1>
      <p className="text-sm text-neutral-500 mb-6">KPIs calculados en servidor (fórmulas V6)</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <BigNumber label="Prospectos" value={k["KPI-001"].valor} unidad={k["KPI-001"].unidad} icon={Users} />
        <BigNumber label="Tasa verificación" value={k["KPI-002"].valor} unidad="%" icon={ShieldCheck} />
        <BigNumber label="Conv. precalificación" value={k["KPI-003"].valor} unidad="%" icon={TrendingUp} />
        <BigNumber label="Pipeline (trámites)" value={k["KPI-016"].valor} unidad="Trámites" icon={PieChart} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Distribution title="Distribución de perfiles" data={k["KPI-004"].valor} />
        <Distribution title="Conversión por modalidad" data={k["KPI-012"].valor} />
        <Distribution title="Motivos de rechazo" data={k["KPI-015"].valor} />
      </div>

      {data.kpis_restringidos?.length > 0 && (
        <div className="bg-[#111] border border-neutral-800 rounded-xl p-5 mb-4" data-testid="kpis-restringidos">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-300 mb-2"><Lock size={16} /> KPIs financieros restringidos</div>
          <div className="text-sm text-neutral-500">Requieren permiso de márgenes/comisiones (PER-013): {data.kpis_restringidos.join(", ")}</div>
        </div>
      )}

      {data.kpis_sin_datos?.length > 0 && (
        <div className="bg-[#111] border border-neutral-800 rounded-xl p-5" data-testid="kpis-sin-datos">
          <div className="text-sm font-semibold text-neutral-300 mb-2">KPIs sin fuente de datos suficiente</div>
          <div className="text-sm text-neutral-500">No se inventan valores. Pendientes de dato fuente: {data.kpis_sin_datos.join(" · ")}</div>
        </div>
      )}
    </div>
  );
}
